import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RefreshCw, Clock, CheckCircle, AlertTriangle, UserCheck, Calendar, Download, Upload, FileSpreadsheet, Grid3X3, List } from 'lucide-react';
import logger from '@/lib/logger';

// ===== Calendar View Component =====
const STATUS_COLORS_MAP = {
    present: 'bg-emerald-500', late: 'bg-amber-500', warning: 'bg-orange-500',
    half_day: 'bg-orange-400', absent: 'bg-red-500', leave: 'bg-violet-500',
    on_leave: 'bg-violet-500', wfh: 'bg-blue-500', holiday: 'bg-slate-400',
    weekend: 'bg-slate-200 dark:bg-slate-700', no_record: 'bg-slate-300',
    future: 'bg-transparent border border-dashed border-slate-300',
};

const CalendarView = ({ filterDept }) => {
    const [calendarData, setCalendarData] = useState(null);
    const [loading, setLoading] = useState(false);
    const now = new Date();
    const [calYear, setCalYear] = useState(now.getFullYear());
    const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
    const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

    const fetchCalendar = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ year: calYear, month: calMonth });
            if (filterDept && filterDept !== 'all') params.append('department', filterDept);
            const res = await api.get(`/hr/attendance/calendar?${params.toString()}`);
            setCalendarData(res.data);
        } catch (e) { logger.error(e); } finally { setLoading(false); }
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchCalendar(); }, [calYear, calMonth, filterDept]);

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    if (!calendarData) return null;

    return (
        <Card data-testid="attendance-calendar-view">
            <CardHeader className="flex-row items-center justify-between gap-4 pb-3">
                <CardTitle className="text-base">Attendance Calendar</CardTitle>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}>&lt;</Button>
                    <span className="text-sm font-medium min-w-[120px] text-center">
                        {new Date(calYear, calMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => { if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}>&gt;</Button>
                </div>
            </CardHeader>
            <CardContent>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 mb-4 text-xs">
                    {[['present','Present'],['late','Late'],['absent','Absent'],['leave','Leave'],['wfh','WFH'],['holiday','Holiday'],['weekend','Weekend'],['no_record','No Record']].map(([key,label]) => (
                        <div key={key} className="flex items-center gap-1">
                            <div className={`w-3 h-3 rounded-sm ${STATUS_COLORS_MAP[key]}`} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
                <ScrollArea className="max-h-[600px]">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="sticky left-0 bg-background z-10 min-w-[150px]">Employee</TableHead>
                                {Array.from({ length: calendarData.total_days }, (_, i) => (
                                    <TableHead key={i+1} className="text-center px-1 min-w-[28px]">
                                        <div className="text-xs">{i+1}</div>
                                        <div className="text-[10px] text-muted-foreground">{DAY_NAMES[new Date(calYear, calMonth - 1, i + 1).getDay() === 0 ? 6 : new Date(calYear, calMonth - 1, i + 1).getDay() - 1]}</div>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(calendarData.employees || []).map(emp => (
                                <TableRow key={emp.employee_id}>
                                    <TableCell className="sticky left-0 bg-background z-10">
                                        <div>
                                            <p className="text-xs font-medium truncate max-w-[140px]">{emp.name}</p>
                                            <p className="text-[10px] text-muted-foreground">{emp.department}</p>
                                        </div>
                                    </TableCell>
                                    {emp.days.map(day => (
                                        <TableCell key={day.day} className="p-0.5 text-center">
                                            <div 
                                                className={`w-6 h-6 rounded-sm mx-auto ${STATUS_COLORS_MAP[day.status] || 'bg-slate-100'}`}
                                                title={`${day.date}: ${day.status}`}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ScrollArea>
            </CardContent>
        </Card>
    );
};

// ===== Muster Roll Component =====
const MusterRollView = ({ filterDept }) => {
    const [musterData, setMusterData] = useState(null);
    const [loading, setLoading] = useState(false);
    const now = new Date();
    const [mrYear, setMrYear] = useState(now.getFullYear());
    const [mrMonth, setMrMonth] = useState(now.getMonth() + 1);

    const fetchMuster = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ year: mrYear, month: mrMonth });
            if (filterDept && filterDept !== 'all') params.append('department', filterDept);
            const res = await api.get(`/hr/attendance/muster-roll?${params.toString()}`);
            setMusterData(res.data);
        } catch (e) { logger.error(e); } finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchMuster(); }, [mrYear, mrMonth, filterDept]);

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    if (!musterData) return null;

    return (
        <div className="space-y-4" data-testid="muster-roll-view">
            <Card>
                <CardHeader className="flex-row items-center justify-between gap-4 pb-3">
                    <div>
                        <CardTitle className="text-base">Muster Roll</CardTitle>
                        <CardDescription className="text-xs">
                            Working Days: {musterData.working_days} | Sundays: {musterData.sundays} | Holidays: {musterData.holidays}
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { if (mrMonth === 1) { setMrMonth(12); setMrYear(y => y - 1); } else setMrMonth(m => m - 1); }}>&lt;</Button>
                        <span className="text-sm font-medium min-w-[120px] text-center">
                            {new Date(mrYear, mrMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => { if (mrMonth === 12) { setMrMonth(1); setMrYear(y => y + 1); } else setMrMonth(m => m + 1); }}>&gt;</Button>
                    </div>
                </CardHeader>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead className="text-center">Present</TableHead>
                            <TableHead className="text-center">Late</TableHead>
                            <TableHead className="text-center">Half Day</TableHead>
                            <TableHead className="text-center">Absent</TableHead>
                            <TableHead className="text-center">WFH</TableHead>
                            <TableHead className="text-center">Leave</TableHead>
                            <TableHead className="text-center">No Record</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                            <TableHead className="text-right">Late (min)</TableHead>
                            <TableHead className="text-right">Att %</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(musterData.muster || []).map(row => (
                            <TableRow key={row.employee_id}>
                                <TableCell>
                                    <div>
                                        <p className="font-medium text-sm">{row.name}</p>
                                        <p className="text-xs text-muted-foreground">{row.employee_code}</p>
                                    </div>
                                </TableCell>
                                <TableCell className="text-sm">{row.department}</TableCell>
                                <TableCell className="text-center text-emerald-600 font-medium">{row.present}</TableCell>
                                <TableCell className="text-center text-amber-600">{row.late || 0}</TableCell>
                                <TableCell className="text-center text-orange-500">{row.half_day || 0}</TableCell>
                                <TableCell className="text-center text-red-600">{row.absent || 0}</TableCell>
                                <TableCell className="text-center text-blue-500">{row.wfh || 0}</TableCell>
                                <TableCell className="text-center text-violet-600">{row.leave || 0}</TableCell>
                                <TableCell className="text-center text-slate-400">{Math.max(0, row.no_record || 0)}</TableCell>
                                <TableCell className="text-right">{row.total_hours}</TableCell>
                                <TableCell className="text-right">{row.total_late_minutes || 0}</TableCell>
                                <TableCell className="text-right">
                                    <Badge variant={row.attendance_pct >= 90 ? 'default' : row.attendance_pct >= 75 ? 'secondary' : 'destructive'} className="text-xs">
                                        {row.attendance_pct}%
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!musterData.muster || musterData.muster.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                                    No attendance data for this month
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
};

const AttendancePage = () => {
    const [attendance, setAttendance] = useState([]);
    const [regularizations, setRegularizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [attendanceData, setAttendanceData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterDept, setFilterDept] = useState('');
    const [activeTab, setActiveTab] = useState('daily');
    const [departments, setDepartments] = useState([]);

    // Manual import state
    const [importMonth, setImportMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [downloading, setDownloading] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, filterDept]);

    useEffect(() => {
        api.get('/departments').then(res => {
            setDepartments(res.data || []);
        }).catch(() => {});
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('date', selectedDate);
            if (filterDept && filterDept !== 'all') params.append('department', filterDept);
            
            const [attendanceRes, regRes] = await Promise.all([
                api.get(`/hr/attendance?${params.toString()}`),
                api.get('/hr/regularization-requests?pending_approval=true')
            ]);
            setAttendanceData(attendanceRes.data || {});
            setAttendance(attendanceRes.data?.records || attendanceRes.data || []);
            setRegularizations(regRes.data || []);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRegApprove = async (requestId, action) => {
        try {
            await api.put(`/hr/regularization-requests/${requestId}/approve?action=${action}`);
            toast.success(`Request ${action}d`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Action failed');
        }
    };

    const handleDownloadTemplate = async () => {
        setDownloading(true);
        try {
            const response = await api.get(`/hr/attendance/template/download?month=${importMonth}`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const [y, m] = importMonth.split('-');
            const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            a.download = `attendance_template_${monthNames[parseInt(m)-1]}_${y}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded successfully');
        } catch (error) {
            logger.error('Download error:', error);
            toast.error(error.response?.data?.detail || 'Failed to download template');
        } finally {
            setDownloading(false);
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('month', importMonth);

            const response = await api.post('/hr/attendance/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const data = response.data;
            toast.success(data.message);
            if (data.errors?.length > 0) {
                data.errors.forEach(err => toast.error(err, { duration: 5000 }));
            }
            fetchData();
        } catch (error) {
            logger.error('Import error:', error);
            toast.error(error.response?.data?.detail || 'Failed to import attendance');
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const getStatusBadge = (record) => {
        const s = record.status;
        if (s === 'wfh') return <Badge className="bg-blue-500 text-white">WFH</Badge>;
        if (s === 'annual_leave') return <Badge className="bg-teal-500 text-white">Annual Leave</Badge>;
        if (s === 'sick_leave') return <Badge className="bg-pink-500 text-white">Sick Leave</Badge>;
        if (s === 'casual_leave') return <Badge className="bg-fuchsia-500 text-white">Casual Leave</Badge>;
        if (s === 'maternity_leave') return <Badge className="bg-rose-500 text-white">Maternity Leave</Badge>;
        if (s === 'paternity_leave') return <Badge className="bg-indigo-500 text-white">Paternity Leave</Badge>;
        if (s === 'unpaid_leave' || s === 'lop') return <Badge className="bg-zinc-500 text-white">Unpaid Leave</Badge>;
        if (s === 'on_leave' || s === 'leave') return <Badge className="bg-violet-500 text-white">On Leave</Badge>;
        if (s === 'weekly_off' || s === 'weekend') return <Badge className="bg-slate-400 text-white">Weekly Off</Badge>;
        if (s === 'holiday') return <Badge className="bg-purple-500 text-white">Holiday</Badge>;
        if (s === 'warning') return <Badge className="bg-orange-600 text-white">Warning</Badge>;
        if (s === 'half_day') return <Badge className="bg-orange-500 text-white">Half Day</Badge>;
        if (s === 'late') return <Badge className="bg-amber-500 text-white">Late</Badge>;
        if (record.late_minutes > 0 && s === 'present') return <Badge className="bg-amber-500 text-white">Late</Badge>;
        if (s === 'present') return <Badge className="bg-green-500 text-white">Present</Badge>;
        return <Badge className="bg-red-500 text-white">Absent</Badge>;
    };

    // Use summary from API if available
    const teamStrength = attendanceData?.team_strength || {};
    const summary = attendanceData?.summary || {};
    const missingEmployees = attendanceData?.missing_employees || [];
    const presentCount = summary.present || attendance.filter(a => a.status === 'present').length;
    const lateCount = summary.late || attendance.filter(a => a.status === 'late').length;
    const warningCount = summary.warning || attendance.filter(a => a.status === 'warning').length;
    const halfDayCount = summary.half_day || attendance.filter(a => a.status === 'half_day').length;
    const absentCount = summary.absent || 0;
    const wfhCount = attendance.filter(a => a.status === 'wfh').length;
    const noRecordCount = summary.no_record || missingEmployees.length;

    // Generate month options for the selector
    const monthOptions = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
        monthOptions.push({ value: val, label });
    }

    return (
        <div className="space-y-6" data-testid="attendance-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
                    <p className="text-muted-foreground">Track daily attendance, regularizations, and manual imports</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-attendance-btn">
                    <RefreshCw className="h-4 w-4 mr-2" />Refresh
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
                <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold">{teamStrength.total || '-'}</p>
                        <p className="text-xs text-muted-foreground">Team Strength</p>
                        <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                            <span>UAE: {teamStrength.uae || 0}</span>
                            <span>IND: {teamStrength.india || 0}</span>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-green-600">{presentCount}</p>
                        <p className="text-xs text-muted-foreground">Present</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-amber-600">{lateCount}</p>
                        <p className="text-xs text-muted-foreground">Late</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-orange-600">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-orange-600">{warningCount}</p>
                        <p className="text-xs text-muted-foreground">Warning</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-orange-400">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-orange-500">{halfDayCount}</p>
                        <p className="text-xs text-muted-foreground">Half Day</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-red-600">{absentCount}</p>
                        <p className="text-xs text-muted-foreground">Absent</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-400">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-blue-500">{wfhCount}</p>
                        <p className="text-xs text-muted-foreground">WFH</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-slate-400">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-slate-600">{noRecordCount}</p>
                        <p className="text-xs text-muted-foreground">No Record</p>
                    </CardContent>
                </Card>
                <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="pt-4 pb-3 px-4">
                        <p className="text-2xl font-bold text-purple-600">{regularizations.length}</p>
                        <p className="text-xs text-muted-foreground">Pending Reg.</p>
                    </CardContent>
                </Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="daily">Daily Attendance</TabsTrigger>
                    <TabsTrigger value="calendar" data-testid="calendar-tab">Calendar View</TabsTrigger>
                    <TabsTrigger value="muster" data-testid="muster-roll-tab">Muster Roll</TabsTrigger>
                    <TabsTrigger value="regularization">Regularizations ({regularizations.length})</TabsTrigger>
                    <TabsTrigger value="import" data-testid="manual-import-tab">Manual Import</TabsTrigger>
                </TabsList>

                {/* Daily Attendance Tab */}
                <TabsContent value="daily" className="mt-4">
                    <Card>
                        <CardHeader className="flex-row items-center justify-between gap-4">
                            <CardTitle>Attendance for {selectedDate}</CardTitle>
                            <div className="flex items-center gap-2">
                                <Input 
                                    type="date" 
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-[180px]"
                                />
                                <Select value={filterDept} onValueChange={setFilterDept}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder="All Departments" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Departments</SelectItem>
                                        {departments.map(d => (
                                            <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employee</TableHead>
                                    <TableHead>Department</TableHead>
                                    <TableHead>Check In</TableHead>
                                    <TableHead>Check Out</TableHead>
                                    <TableHead>Work Hours</TableHead>
                                    <TableHead>Late (mins)</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Remarks</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {attendance.map((record) => (
                                    <TableRow key={record.id}>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">{record.employee_name}</p>
                                                <p className="text-xs text-muted-foreground">{record.employee_code}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>{record.department}</TableCell>
                                        <TableCell>{record.biometric_in || '-'}</TableCell>
                                        <TableCell>{record.biometric_out || '-'}</TableCell>
                                        <TableCell>{record.total_work_hours?.toFixed(1) || '-'} hrs</TableCell>
                                        <TableCell>
                                            {record.late_minutes > 0 ? (
                                                <span className="text-amber-600 font-medium">{record.late_minutes}</span>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>{getStatusBadge(record)}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`text-[10px] ${record.source === 'self_punch' ? 'border-blue-300 text-blue-600' : 'border-green-300 text-green-600'}`}>
                                                {record.source === 'self_punch' ? 'SSHR' : record.source === 'biocloud' ? 'BioCloud' : record.source || 'BioCloud'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                                            {record.half_day_reason || record.shift_name || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {attendance.length === 0 && missingEmployees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                            No attendance records for this date
                                        </TableCell>
                                    </TableRow>
                                )}
                                {/* Missing Employees — No Punch Record */}
                                {missingEmployees.map((emp) => (
                                    <TableRow key={`missing-${emp.employee_code}`} className="bg-slate-50/50 dark:bg-slate-900/20">
                                        <TableCell>
                                            <div>
                                                <p className="font-medium text-muted-foreground">{emp.employee_name}</p>
                                                <p className="text-xs text-muted-foreground">{emp.employee_code}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">{emp.department}</TableCell>
                                        <TableCell className="text-muted-foreground">-</TableCell>
                                        <TableCell className="text-muted-foreground">-</TableCell>
                                        <TableCell className="text-muted-foreground">-</TableCell>
                                        <TableCell className="text-muted-foreground">-</TableCell>
                                        <TableCell><Badge className="bg-slate-500 text-white">No Record</Badge></TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{emp.designation || 'Did not punch in'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                {/* Calendar View Tab */}
                <TabsContent value="calendar" className="mt-4">
                    <CalendarView filterDept={filterDept} />
                </TabsContent>

                {/* Muster Roll Tab */}
                <TabsContent value="muster" className="mt-4">
                    <MusterRollView filterDept={filterDept} />
                </TabsContent>

                {/* Regularization Tab */}
                <TabsContent value="regularization" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Pending Regularization Requests</CardTitle>
                        </CardHeader>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employee</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Original</TableHead>
                                    <TableHead>Corrected</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead>Level</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {regularizations.map((req) => (
                                    <TableRow key={req.id}>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">{req.employee_name}</p>
                                                <p className="text-xs text-muted-foreground">{req.employee_code}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>{req.date}</TableCell>
                                        <TableCell className="capitalize">{req.type?.replace(/_/g, ' ')}</TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                <p>In: {req.original_in || '-'}</p>
                                                <p>Out: {req.original_out || '-'}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                <p>In: {req.corrected_in || '-'}</p>
                                                <p>Out: {req.corrected_out || '-'}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="max-w-[150px] truncate">{req.reason}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{req.current_approver_level}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button size="sm" variant="outline" className="text-green-600"
                                                    onClick={() => handleRegApprove(req.id, 'approve')}>
                                                    Approve
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-red-600"
                                                    onClick={() => handleRegApprove(req.id, 'reject')}>
                                                    Reject
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {regularizations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                            No pending regularization requests
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                {/* Manual Import Tab */}
                <TabsContent value="import" className="mt-4">
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {/* Step 1: Download Template */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Download className="h-5 w-5 text-blue-600" />
                                    Step 1: Download Template
                                </CardTitle>
                                <CardDescription>
                                    Download the pre-filled template with daily punch-in/out columns.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Select Month</label>
                                    <Select value={importMonth} onValueChange={setImportMonth}>
                                        <SelectTrigger data-testid="import-month-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {monthOptions.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button 
                                    onClick={handleDownloadTemplate} 
                                    disabled={downloading}
                                    className="w-full"
                                    data-testid="download-attendance-template-btn"
                                >
                                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                                    {downloading ? 'Generating...' : 'Download XLSX Template'}
                                </Button>
                                <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
                                    <p className="font-medium">Template includes:</p>
                                    <ul className="list-disc list-inside space-y-0.5">
                                        <li>Employee ID, Name, Department, Salary</li>
                                        <li>Daily In/Out columns (pre-filled from biometric)</li>
                                        <li>Auto-calculated Full/Half/Absent days</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Step 2: Upload Filled Sheet */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Upload className="h-5 w-5 text-green-600" />
                                    Step 2: Upload Filled Sheet
                                </CardTitle>
                                <CardDescription>
                                    Fill daily punch times and upload the completed sheet.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Import Month</label>
                                    <Select value={importMonth} onValueChange={setImportMonth}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {monthOptions.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".xlsx,.xls"
                                    onChange={handleImportFile}
                                    className="hidden"
                                    data-testid="attendance-file-input"
                                />
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importing}
                                    variant="outline"
                                    className="w-full border-dashed border-2 h-20"
                                    data-testid="upload-attendance-btn"
                                >
                                    <div className="flex flex-col items-center gap-1">
                                        <Upload className="h-5 w-5" />
                                        <span className="text-sm">{importing ? 'Importing...' : 'Click to upload filled XLSX'}</span>
                                    </div>
                                </Button>
                                <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
                                    <p className="font-medium">After import:</p>
                                    <ul className="list-disc list-inside space-y-0.5">
                                        <li>Daily attendance records created per employee</li>
                                        <li>Half/Full day auto-calculated from hours</li>
                                        <li>Previous manual imports for same month replaced</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Biocloud Upload */}
                        <Card className="border-emerald-500/30">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <UserCheck className="h-5 w-5 text-emerald-600" />
                                    BioCloud Upload
                                </CardTitle>
                                <CardDescription>
                                    Upload the BioCloud attendance export directly to sync biometric punch data.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <input
                                    type="file"
                                    id="biocloud-file"
                                    accept=".xlsx,.xls"
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        try {
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            toast.info('Processing BioCloud export...');
                                            const res = await api.post('/hr/biocloud/upload-attendance', formData, {
                                                headers: { 'Content-Type': 'multipart/form-data' }
                                            });
                                            const d = res.data;
                                            toast.success(d.message, { duration: 8000 });
                                            if (d.unmatched_employees?.length > 0) {
                                                toast.warning(`${d.unmatched_employees.length} badge numbers not mapped: ${d.unmatched_employees.map(u => u.badge + ' ' + u.name).join(', ')}`, { duration: 10000 });
                                            }
                                            fetchData();
                                        } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Upload failed');
                                        }
                                        e.target.value = '';
                                    }}
                                    className="hidden"
                                    data-testid="biocloud-file-input"
                                />
                                <Button
                                    onClick={() => document.getElementById('biocloud-file')?.click()}
                                    variant="outline"
                                    className="w-full border-dashed border-2 h-20 border-emerald-500/40 hover:border-emerald-500"
                                    data-testid="upload-biocloud-btn"
                                >
                                    <div className="flex flex-col items-center gap-1">
                                        <UserCheck className="h-5 w-5 text-emerald-600" />
                                        <span className="text-sm">Upload BioCloud XLSX Export</span>
                                    </div>
                                </Button>
                                <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
                                    <p className="font-medium">How it works:</p>
                                    <ul className="list-disc list-inside space-y-0.5">
                                        <li>Upload the raw export from BioCloud</li>
                                        <li>First In & Last Out auto-extracted per day</li>
                                        <li>Matched by Badge Number to mapped employees</li>
                                        <li>Pre-fills the attendance template</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AttendancePage;
